import {
  QUALITY_PROFILE_NAMES,
  type AdaptiveQualityConfig,
  type QualityProfile,
  type QualityProfileName,
} from './types';

export const DEFAULT_QUALITY_PROFILES: readonly QualityProfile[] = Object.freeze([
  Object.freeze({
    name: 'ULTRA',
    maxWidth: 3840,
    maxHeight: 2160,
    maxBitrateBps: 18_000_000,
    minBitrateBps: 8_000_000,
    maxFramesPerSecond: 30,
    reducedFramesPerSecond: 15,
    upgradeAvailableBitrateBps: 22_000_000,
  }),
  Object.freeze({
    name: 'HIGH',
    maxWidth: 2560,
    maxHeight: 1440,
    maxBitrateBps: 10_000_000,
    minBitrateBps: 5_000_000,
    maxFramesPerSecond: 30,
    reducedFramesPerSecond: 15,
    upgradeAvailableBitrateBps: 12_000_000,
  }),
  Object.freeze({
    name: 'STANDARD',
    maxWidth: 1920,
    maxHeight: 1080,
    maxBitrateBps: 6_000_000,
    minBitrateBps: 3_000_000,
    maxFramesPerSecond: 30,
    reducedFramesPerSecond: 15,
    upgradeAvailableBitrateBps: 7_000_000,
  }),
  Object.freeze({
    name: 'MEDIUM',
    maxWidth: 1280,
    maxHeight: 720,
    maxBitrateBps: 3_000_000,
    minBitrateBps: 1_500_000,
    maxFramesPerSecond: 24,
    reducedFramesPerSecond: 12,
    upgradeAvailableBitrateBps: 3_800_000,
  }),
  Object.freeze({
    name: 'LOW',
    maxWidth: 854,
    maxHeight: 480,
    maxBitrateBps: 1_500_000,
    minBitrateBps: 700_000,
    maxFramesPerSecond: 20,
    reducedFramesPerSecond: 10,
    upgradeAvailableBitrateBps: 1_900_000,
  }),
  Object.freeze({
    name: 'SURVIVAL',
    maxWidth: 640,
    maxHeight: 360,
    maxBitrateBps: 700_000,
    minBitrateBps: 250_000,
    maxFramesPerSecond: 15,
    reducedFramesPerSecond: 8,
    upgradeAvailableBitrateBps: 900_000,
  }),
]);

export const DEFAULT_ADAPTIVE_QUALITY_CONFIG: Readonly<AdaptiveQualityConfig> = Object.freeze({
  profiles: DEFAULT_QUALITY_PROFILES,
  initialProfile: 'STANDARD',
  movingAverageWindowSize: 5,
  minimumSamples: 3,
  degradeConsecutiveSamples: 3,
  severeDegradeConsecutiveSamples: 2,
  upgradeConsecutiveSamples: 8,
  degradeCooldownMs: 5_000,
  upgradeCooldownMs: 20_000,
  packetLossDegradePercentage: 5,
  packetLossSeverePercentage: 12,
  packetLossRecoveryPercentage: 1.5,
  rttDegradeMs: 300,
  rttSevereMs: 650,
  rttRecoveryMs: 180,
  bandwidthDegradeRatio: 0.9,
  bandwidthSevereRatio: 0.6,
  upgradeHeadroomRatio: 1.3,
  bitrateUtilizationRatio: 0.8,
  degradationBitrateReductionRatio: 0.8,
  minimumBitrateChangeRatio: 0.1,
  cpuLimitedSampleRatio: 0.5,
  bandwidthLimitedSampleRatio: 0.5,
  lanUpgradeThresholdMultiplier: 0.85,
  lanBitrateMultiplier: 1.1,
  turnUpgradeThresholdMultiplier: 1.25,
  turnBitrateMultiplier: 0.8,
  turnMaximumProfile: 'STANDARD',
  unknownPathMaximumProfile: 'HIGH',
});

export function createAdaptiveQualityConfig(
  overrides: Partial<AdaptiveQualityConfig> = {},
): Readonly<AdaptiveQualityConfig> {
  const profiles = Object.freeze(
    [...(overrides.profiles ?? DEFAULT_QUALITY_PROFILES)].map((profile) =>
      Object.freeze({ ...profile }),
    ),
  );
  const config = Object.freeze({
    ...DEFAULT_ADAPTIVE_QUALITY_CONFIG,
    ...overrides,
    profiles,
  });
  validateAdaptiveQualityConfig(config);
  return config;
}

export function validateAdaptiveQualityConfig(config: AdaptiveQualityConfig): void {
  const names = config.profiles.map(({ name }) => name);
  if (
    config.profiles.length !== QUALITY_PROFILE_NAMES.length ||
    QUALITY_PROFILE_NAMES.some((name, index) => names[index] !== name)
  ) {
    throw new TypeError(`profiles must be ordered ${QUALITY_PROFILE_NAMES.join(', ')}`);
  }

  for (const [index, profile] of config.profiles.entries()) {
    const values = [
      profile.maxWidth,
      profile.maxHeight,
      profile.maxBitrateBps,
      profile.minBitrateBps,
      profile.maxFramesPerSecond,
      profile.reducedFramesPerSecond,
      profile.upgradeAvailableBitrateBps,
    ];
    if (values.some((value) => !Number.isFinite(value) || value <= 0)) {
      throw new TypeError(`profile ${profile.name} values must be positive finite numbers`);
    }
    if (profile.minBitrateBps > profile.maxBitrateBps) {
      throw new TypeError(`profile ${profile.name} minimum bitrate exceeds its maximum`);
    }
    if (profile.reducedFramesPerSecond > profile.maxFramesPerSecond) {
      throw new TypeError(`profile ${profile.name} reduced FPS exceeds its maximum`);
    }
    const previous = config.profiles[index - 1];
    if (
      previous &&
      (profile.maxWidth > previous.maxWidth || profile.maxHeight > previous.maxHeight)
    ) {
      throw new TypeError('profile resolutions must be ordered from highest to lowest');
    }
  }

  const positiveIntegers = [
    config.movingAverageWindowSize,
    config.minimumSamples,
    config.degradeConsecutiveSamples,
    config.severeDegradeConsecutiveSamples,
    config.upgradeConsecutiveSamples,
  ];
  if (positiveIntegers.some((value) => !Number.isInteger(value) || value < 1)) {
    throw new TypeError('sample windows and streak thresholds must be positive integers');
  }
  if (config.minimumSamples > config.movingAverageWindowSize) {
    throw new TypeError('minimumSamples cannot exceed movingAverageWindowSize');
  }

  const nonNegative = [
    config.degradeCooldownMs,
    config.upgradeCooldownMs,
    config.packetLossDegradePercentage,
    config.packetLossSeverePercentage,
    config.packetLossRecoveryPercentage,
    config.rttDegradeMs,
    config.rttSevereMs,
    config.rttRecoveryMs,
  ];
  if (nonNegative.some((value) => !Number.isFinite(value) || value < 0)) {
    throw new TypeError('cooldowns and metric thresholds must be non-negative finite numbers');
  }

  const ratios = [
    config.bandwidthDegradeRatio,
    config.bandwidthSevereRatio,
    config.upgradeHeadroomRatio,
    config.bitrateUtilizationRatio,
    config.degradationBitrateReductionRatio,
    config.minimumBitrateChangeRatio,
    config.cpuLimitedSampleRatio,
    config.bandwidthLimitedSampleRatio,
    config.lanUpgradeThresholdMultiplier,
    config.lanBitrateMultiplier,
    config.turnUpgradeThresholdMultiplier,
    config.turnBitrateMultiplier,
  ];
  if (ratios.some((value) => !Number.isFinite(value) || value <= 0)) {
    throw new TypeError('quality ratios must be positive finite numbers');
  }
  if (config.cpuLimitedSampleRatio > 1 || config.bandwidthLimitedSampleRatio > 1) {
    throw new TypeError('limitation sample ratios cannot exceed 1');
  }
  if (config.degradationBitrateReductionRatio > 1) {
    throw new TypeError('degradationBitrateReductionRatio cannot exceed 1');
  }
  if (
    config.bandwidthSevereRatio > config.bandwidthDegradeRatio ||
    config.bitrateUtilizationRatio > 1 ||
    config.minimumBitrateChangeRatio > 1 ||
    config.upgradeHeadroomRatio < 1
  ) {
    throw new TypeError('bandwidth ratios are not ordered safely');
  }
  if (
    config.packetLossRecoveryPercentage > config.packetLossDegradePercentage ||
    config.packetLossDegradePercentage > config.packetLossSeverePercentage ||
    config.packetLossSeverePercentage > 100
  ) {
    throw new TypeError('packet-loss thresholds must be ordered recovery, degrade, severe');
  }
  if (config.rttRecoveryMs > config.rttDegradeMs || config.rttDegradeMs > config.rttSevereMs) {
    throw new TypeError('RTT thresholds must be ordered recovery, degrade, severe');
  }

  profileIndex(config, config.initialProfile);
  profileIndex(config, config.turnMaximumProfile);
  profileIndex(config, config.unknownPathMaximumProfile);
}

export function profileIndex(
  config: Pick<AdaptiveQualityConfig, 'profiles'>,
  name: QualityProfileName,
): number {
  const index = config.profiles.findIndex((profile) => profile.name === name);
  if (index === -1) {
    throw new TypeError(`unknown quality profile: ${name}`);
  }
  return index;
}
